import React, { ReactNode } from 'react'
import {
  Box,
  Flex,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { isEmpty } from 'lodash'
import { JointOwnerTaxIds } from './index'
import { useClientRect } from '../../hooks'
import { InfoPopover } from '../InfoPopover'

export interface AccountTransferTableProps {
  deliveringAccountNumber: ReactNode
  receivingAccountNumber: ReactNode
  deliveringParticipantId: ReactNode
  receivingParticipantId: ReactNode
  deliveringAccountTitle: ReactNode
  receivingAccountTitle: ReactNode
  deliveringPrimaryTaxIdWithType: ReactNode
  receivingPrimaryTaxIdWithType: ReactNode
  deliveringSecondaryTaxId: ReactNode
  receivingSecondaryTaxIds: string[]
  clientReferenceId: ReactNode
  memo: ReactNode
  title: ReactNode
  showPopoverIcons?: boolean
  rowHeightOverrideValue?: string
  transferType: ReactNode
}

const AccountTransferTable = ({
  deliveringAccountNumber,
  receivingAccountNumber,
  deliveringParticipantId,
  receivingParticipantId,
  deliveringAccountTitle,
  receivingAccountTitle,
  deliveringPrimaryTaxIdWithType,
  receivingPrimaryTaxIdWithType,
  deliveringSecondaryTaxId,
  receivingSecondaryTaxIds,
  clientReferenceId,
  memo,
  title,
  showPopoverIcons = true,
  rowHeightOverrideValue,
  transferType,
}: AccountTransferTableProps) => {
  const [rowRect, rowRef] = useClientRect()
  const defaultRowHeight = rowHeightOverrideValue
    ? rowHeightOverrideValue
    : rowRect?.height || '50px'

  return (
    <Box width="60%">
      <Box bg="gray.600" color="apex.white" p={3}>
        <Text data-testid="title" fontWeight="bold">
          {title}
        </Text>
      </Box>
      <HStack
        p={3}
        marginBottom="1.4286rem"
        borderBottomColor="gray.400"
        borderBottomWidth="1px"
        alignItems="start"
      >
        <Text fontWeight="semibold" width="20%">
          Transfer Type *
        </Text>
        {transferType}
      </HStack>
      <Table
        sx={{
          'td:first-of-type': {
            verticalAlign: 'top',
            position: 'relative',
            top: '10px',
          },
          td: {
            verticalAlign: 'middle',
            wordBreak: 'break-word',
          },
        }}
      >
        <Thead>
          <Tr>
            <Th w="20%">Account Information</Th>
            <Th w="40%">Delivering Account</Th>
            <Th w="40%">Receiving Account</Th>
          </Tr>
        </Thead>
        <Tbody>
          <Tr
            data-testid="account-number-row"
            ref={rowRef}
            height={defaultRowHeight}
          >
            <Td fontWeight="semibold">Account Number *</Td>
            <Td data-testid="delivering-account-number">
              {deliveringAccountNumber}
            </Td>
            <Td data-testid="receiving-account-number">
              {receivingAccountNumber}
            </Td>
          </Tr>
          <Tr data-testid="participant-id-row" height={defaultRowHeight}>
            <Td fontWeight="semibold">
              <Flex justifyContent="space-between">
                Participant ID *
                {showPopoverIcons && (
                  <InfoPopover
                    title="Participant ID"
                    description="Please provide the 4 digit code associated with the account's clearing firm number; input “0158” for internal transfers."
                  />
                )}
              </Flex>
            </Td>
            <Td data-testid="delivering-participant-id">
              {deliveringParticipantId}
            </Td>
            <Td data-testid="receiving-participant-id">
              {receivingParticipantId}
            </Td>
          </Tr>
          <Tr data-testid="account-title-row" height={defaultRowHeight}>
            <Td fontWeight="semibold">
              <Flex justifyContent="space-between">
                Account Title *
                {showPopoverIcons && (
                  <InfoPopover
                    title="Account Title"
                    description="If account registrations do not match, it is suggested that the request be submitted manually. Please provide a Letter of Instruction authorizing the transfer, and any additional supporting documents with the transfer form."
                  />
                )}
              </Flex>
            </Td>
            <Td data-testid="delivering-account-title">
              {deliveringAccountTitle}
            </Td>
            <Td data-testid="receiving-account-title">
              {receivingAccountTitle}
            </Td>
          </Tr>
          <Tr data-testid="primary-tax-id-row" height={defaultRowHeight}>
            <Td>
              <Flex justifyContent="space-between">
                Primary Tax ID
                {showPopoverIcons && (
                  <InfoPopover
                    title="Primary Tax ID"
                    description="Check the box if the Primary account holder does not have a valid Social Security Number (SSN) or IRS issued Tax ID Number (TIN)."
                  />
                )}
              </Flex>
            </Td>
            <Td data-testid="delivering-primary-tax-id-with-type">
              {deliveringPrimaryTaxIdWithType}
            </Td>
            <Td data-testid="receiving-primary-tax-id-with-type">
              {receivingPrimaryTaxIdWithType}
            </Td>
          </Tr>
          {!isEmpty(receivingSecondaryTaxIds) && (
            <Tr data-testid="secondary-tax-id-row" height={defaultRowHeight}>
              <Td>
                <Flex justifyContent="space-between">
                  Secondary Tax ID
                  {showPopoverIcons && (
                    <InfoPopover
                      title="Secondary Tax ID"
                      description="Check the box if the Secondary account holder does not have a valid Social Security Number (SSN) or IRS issued Tax ID Number (TIN)."
                    />
                  )}
                </Flex>
              </Td>
              <Td data-testid="delivering-secondary-tax-id">
                {deliveringSecondaryTaxId}
              </Td>
              <Td data-testid="receiving-secondary-tax-id">
                <JointOwnerTaxIds
                  receivingSecondaryTaxIds={receivingSecondaryTaxIds}
                />
              </Td>
            </Tr>
          )}
          <Tr data-testid="client-reference-id-row" height={defaultRowHeight}>
            <Td>Client Reference ID</Td>
            <Td data-testid="client-reference-id" colSpan={2}>
              {clientReferenceId}
            </Td>
          </Tr>
          <Tr data-testid="memo-row" height={defaultRowHeight}>
            <Td>
              <Flex justifyContent="space-between">
                Memo
                {showPopoverIcons && (
                  <InfoPopover
                    title="Memo"
                    description="Memo will be sent to the delivering broker to assist in processing and approval."
                  />
                )}
              </Flex>
            </Td>
            <Td data-testid="memo" colSpan={2}>
              {memo}
            </Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  )
}

export default AccountTransferTable
